const createClientKey = (prefix = 'tmp') => `${prefix}-${Date.now()}-${Math.random().toString(16).slice(2, 10)}`;

export const INQUIRY_ATTRIBUTE_TYPE_OPTIONS = [
  { value: 'string', label: 'Kort tekst' },
  { value: 'text', label: 'Lang tekst' },
  { value: 'integer', label: 'Heltall' },
  { value: 'boolean', label: 'Ja/nei' },
  { value: 'select', label: 'Valgliste' },
  { value: 'product', label: 'Produkt valg' },
  { value: 'date', label: 'Dato' },
  { value: 'datetime', label: 'Dato og tid' },
  { value: 'location', label: 'Lokasjon' },
];

export const INQUIRY_ATTRIBUTE_SOURCE_OPTIONS = [
  { value: 'none', label: 'Ingen' },
  { value: 'products', label: 'Produkter' },
];

export const MAX_SELECT_OPTION_LENGTH = 120;

export const deriveAttributeKeyFromLabel = (label) => (
  String(label || '')
    .trim()
    .toLowerCase()
    .replace(/æ/g, 'ae')
    .replace(/ø/g, 'o')
    .replace(/å/g, 'a')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .replace(/_+/g, '_')
);

export const getInquiryAttributeEntryKey = (field, index) => (
  field?.clientKey || `inquiry-attribute-${field?.key || index}`
);

export const getInquiryAttributeTypeLabel = (type) => (
  INQUIRY_ATTRIBUTE_TYPE_OPTIONS.find((option) => option.value === type)?.label || type || 'Kort tekst'
);

const normalizeInquiryAttributeSourceType = (field) => {
  if (field?.type === 'product') return 'products';
  const source = field?.sourceConfig?.source || field?.source_config?.source || field?.sourceType || 'none';
  return source === 'products' ? 'products' : 'none';
};

export const normalizeSelectOptionsDraft = (value) => {
  const source = Array.isArray(value)
    ? value
    : typeof value === 'string'
      ? value.split(/[,\n]+/)
      : [];

  const normalized = [];
  const seen = new Set();
  source.forEach((entry) => {
    const option = String(entry || '').trim().slice(0, MAX_SELECT_OPTION_LENGTH);
    if (!option || seen.has(option)) return;
    seen.add(option);
    normalized.push(option);
  });
  return normalized;
};

export const createEmptyInquiryAttributeDraft = (defaults = {}) => ({
  clientKey: createClientKey('inquiry-attribute'),
  key: '',
  label: '',
  type: 'string',
  options: [],
  placeholder: '',
  helpText: '',
  defaultValue: '',
  required: false,
  collectInChat: true,
  showInInboxList: false,
  useAsDisplayName: false,
  sourceType: 'none',
  sourceValueMode: 'product_id',
  ...defaults,
});

export const createEmptyInquiryTypeDraft = () => ({
  id: null,
  clientKey: createClientKey('inquiry-type'),
  name: '',
  description: '',
  status: 'active',
  isDefault: false,
  sortOrder: 0,
  assignedBotIds: [],
  attributes: [],
});

const mapInquiryAttributeDraft = (field, index) => ({
  ...createEmptyInquiryAttributeDraft(),
  clientKey: field?.clientKey || field?.client_key || `inquiry-attribute-${field?.id ?? field?.key ?? index}`,
  key: field?.key || '',
  label: field?.label || '',
  type: field?.type || 'string',
  options: normalizeSelectOptionsDraft(field?.options ?? field?.optionsText),
  placeholder: field?.placeholder || '',
  helpText: field?.helpText || field?.help_text || '',
  defaultValue:
    typeof field?.defaultValue === 'boolean'
      ? String(field.defaultValue)
      : field?.type === 'location'
        ? (
          field?.defaultValue?.address
          || field?.defaultValue?.formatted_address
          || field?.defaultValue?.formattedAddress
          || field?.default_value?.address
          || field?.default_value?.formatted_address
          || field?.default_value?.formattedAddress
          || ''
        )
        : (field?.defaultValue ?? field?.default_value ?? ''),
  required: field?.required === true,
  collectInChat: field?.collectInChat !== false && field?.collect_in_chat !== false,
  showInInboxList: field?.showInInboxList === true || field?.show_in_inbox_list === true || field?.showInList === true,
  useAsDisplayName: field?.useAsDisplayName === true || field?.use_as_display_name === true,
  sourceType: normalizeInquiryAttributeSourceType(field),
  sourceValueMode: field?.sourceConfig?.value_mode || field?.source_config?.value_mode || field?.sourceValueMode || 'product_id',
  order: Number.isFinite(Number(field?.order)) ? Number(field.order) : index,
});

export const normalizeInquiryTypeItem = (item, index = 0) => ({
  ...createEmptyInquiryTypeDraft(),
  id: item?.id ?? null,
  clientKey: item?.id ? `inquiry-type-${item.id}` : createClientKey('inquiry-type'),
  name: item?.name || '',
  description: item?.description || '',
  status: item?.status || 'active',
  isDefault: item?.is_default === true || item?.isDefault === true,
  sortOrder: Number.isFinite(Number(item?.sort_order ?? item?.sortOrder))
    ? Number(item?.sort_order ?? item?.sortOrder)
    : index,
  assignedBotIds: Array.isArray(item?.assigned_bot_ids)
    ? item.assigned_bot_ids.map((entry) => Number(entry)).filter((entry) => Number.isInteger(entry) && entry > 0)
    : Array.isArray(item?.assignedBotIds)
      ? item.assignedBotIds.map((entry) => Number(entry)).filter((entry) => Number.isInteger(entry) && entry > 0)
      : [],
  attributes: Array.isArray(item?.attributes)
    ? item.attributes.map((field, fieldIndex) => mapInquiryAttributeDraft(field, fieldIndex))
    : [],
});

const buildInquiryAttributePayload = (field, index) => {
  const normalizedType = String(field?.type || 'string').trim() || 'string';
  const normalizedOptions = normalizeSelectOptionsDraft(field?.options);
  const stableKey = String(field?.key || '').trim().toLowerCase() || deriveAttributeKeyFromLabel(field?.label);
  const rawDefaultValue = field?.defaultValue;
  const isProductSelector = normalizedType === 'product';
  const sourceType = isProductSelector || field?.sourceType === 'products' ? 'products' : 'none';
  let defaultValue = isProductSelector ? null : rawDefaultValue;
  if (normalizedType === 'boolean') {
    defaultValue = rawDefaultValue === 'true' || rawDefaultValue === true
      ? true
      : rawDefaultValue === 'false' || rawDefaultValue === false
        ? false
        : null;
  } else if (normalizedType === 'integer') {
    defaultValue = rawDefaultValue === '' || rawDefaultValue === null || rawDefaultValue === undefined
      ? null
      : Number(rawDefaultValue);
  } else if (rawDefaultValue === '') {
    defaultValue = null;
  }

  return {
    key: stableKey,
    label: String(field?.label || '').trim(),
    type: isProductSelector ? 'product' : normalizedType,
    required: field?.required === true,
    collectInChat: field?.collectInChat !== false,
    showInInboxList: isProductSelector ? false : field?.showInInboxList === true,
    useAsDisplayName: isProductSelector ? false : field?.useAsDisplayName === true,
    options: sourceType === 'products'
      ? []
      : (
        normalizedType === 'select'
          ? normalizedOptions
          : []
      ),
    placeholder: String(field?.placeholder || '').trim() || null,
    helpText: String(field?.helpText || '').trim() || null,
    defaultValue,
    order: index,
    sourceConfig: sourceType === 'products'
      ? {
        source: 'products',
        value_mode: isProductSelector ? 'product_id' : field?.sourceValueMode === 'product_name' ? 'product_name' : 'product_id',
      }
      : null,
  };
};

export const buildInquiryTypePayload = (draft) => ({
  name: String(draft?.name || '').trim(),
  description: String(draft?.description || '').trim() || null,
  status: draft?.status || 'active',
  is_default: draft?.isDefault === true,
  sort_order: Number.isFinite(Number(draft?.sortOrder)) ? Number(draft.sortOrder) : 0,
  assigned_bot_ids: Array.isArray(draft?.assignedBotIds)
    ? draft.assignedBotIds.map((entry) => Number(entry)).filter((entry) => Number.isInteger(entry) && entry > 0)
    : [],
  attributes: Array.isArray(draft?.attributes)
    ? draft.attributes.map((field, index) => buildInquiryAttributePayload(field, index))
    : [],
});

export const validateInquiryTypeDraft = (draft) => {
  if (!String(draft?.name || '').trim()) {
    return 'Henvendelsestypen må ha et navn.';
  }

  const fields = Array.isArray(draft?.attributes) ? draft.attributes : [];
  const seenKeys = new Set();
  let displayNameCount = 0;
  let productSelectorCount = 0;
  for (let index = 0; index < fields.length; index += 1) {
    const field = fields[index] || {};
    const label = String(field.label || '').trim();
    const key = String(field.key || '').trim().toLowerCase() || deriveAttributeKeyFromLabel(label);
    const type = String(field.type || 'string').trim() || 'string';
    const sourceType = type === 'product' || field?.sourceType === 'products' ? 'products' : 'none';

    if (!label) {
      return `Attributt ${index + 1} mangler etikett.`;
    }
    if (seenKeys.has(key)) {
      return `Nøkkelen "${key}" er brukt flere ganger i henvendelsestypen.`;
    }
    seenKeys.add(key);

    if (field.useAsDisplayName === true) {
      displayNameCount += 1;
      if (displayNameCount > 1) {
        return 'Kun ett attributt kan brukes som Visningsnavn.';
      }
    }

    if (type === 'product') {
      productSelectorCount += 1;
      if (productSelectorCount > 1) {
        return 'Kun ett attributt kan være Produkt valg.';
      }
    }

    if (sourceType === 'products' && type !== 'select' && type !== 'product') {
      return `Attributtet "${label}" må være en valgliste eller Produkt valg når det er koblet til produkter.`;
    }

    if (sourceType !== 'products' && type === 'select') {
      const options = normalizeSelectOptionsDraft(field.options);
      if (options.length === 0) {
        return `Valglisten for "${label}" må ha minst ett alternativ.`;
      }
    }

    if (type === 'integer' && field.defaultValue !== '' && field.defaultValue !== null && field.defaultValue !== undefined) {
      const parsed = Number(field.defaultValue);
      if (!Number.isInteger(parsed)) {
        return `Standardverdien for "${label}" må være et heltall.`;
      }
    }
  }

  return null;
};
